import { Link } from "react-router";
import { Alerta, Badge, Button, DataList, Dialog, MetricCard, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { OpcionSelect } from "@/components";
import type { LineaCaja, Moneda, TipoLinea } from "@/lib/api";
import { avisoNegativos, detalleTotalUsd, ETIQUETA_TIPO, OPCIONES_TIPO, rutaReferencia } from "@/lib/caja";
import { parseMonto } from "@/lib/gastos";
import { formatFecha, formatMoneda, formatNumero } from "@/lib/format";
import { FormularioCambio } from "./FormularioCambio";
import { PanelCajas } from "./PanelCajas";
import { useCajaTab } from "./useCajaTab";

const OPCIONES_MONEDA_CAJA: OpcionSelect[] = [
  { valor: "", etiqueta: "Todas las monedas" },
  { valor: "ARS", etiqueta: "ARS" },
  { valor: "USD", etiqueta: "USD" },
];

export function CajaTab() {
  const t = useCajaTab();
  const d = t.dialogo;
  const s = t.saldos;
  const l = t.libro;

  const columnas = [
    { clave: "fecha", titulo: "Fecha", render: (li: LineaCaja) => formatFecha(li.fecha) },
    { clave: "tipo", titulo: "Tipo", render: (li: LineaCaja) => <Badge tono="neutro">{ETIQUETA_TIPO[li.tipo]}</Badge> },
    {
      clave: "concepto",
      titulo: "Concepto",
      render: (li: LineaCaja) => {
        const ruta = rutaReferencia(li.referencia);
        return ruta ? (
          <Link to={ruta} className="text-primario underline-offset-2 hover:underline">
            {li.concepto}
          </Link>
        ) : (
          li.concepto
        );
      },
    },
    { clave: "ingreso", titulo: "Ingreso", render: (li: LineaCaja) => (parseMonto(li.ingreso) !== null ? formatMoneda(li.ingreso, li.moneda) : "—") },
    { clave: "egreso", titulo: "Egreso", render: (li: LineaCaja) => (parseMonto(li.egreso) !== null ? formatMoneda(li.egreso, li.moneda) : "—") },
    { clave: "tc", titulo: "Tipo de cambio", render: (li: LineaCaja) => (li.tipo_cambio ? formatNumero(li.tipo_cambio) : "—") },
    ...(t.mostrarCaja ? [{ clave: "caja", titulo: "Caja", render: (li: LineaCaja) => li.caja ?? "—" }] : []),
    { clave: "usd", titulo: "USD", render: (li: LineaCaja) => (li.monto_usd === null ? "—" : formatMoneda(li.monto_usd, "USD")) },
    { clave: "saldo", titulo: "Saldo", render: (li: LineaCaja) => formatMoneda(li.saldo, li.moneda) },
  ];

  const acciones = (li: LineaCaja) => {
    if (li.tipo === "transferencia" && li.referencia) {
      // Una transferencia son dos líneas: las acciones van en la que sale (o en la que se ve, mirando una sola caja).
      if (!li.clave.endsWith("-sale") && !t.filtros.cajaId) return null;
      const id = li.referencia.id;
      return (
        <Row espacio={2}>
          <Button variante="secundario" tamanio="sm" cargando={t.panel.cargandoTransferencia} aria-label={`Editar ${li.concepto}`} onClick={() => void t.panel.abrirEditarTransferencia(id)}>
            Editar
          </Button>
          <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${li.concepto}`} onClick={() => void t.panel.abrirBorrarTransferencia(id)}>
            Borrar
          </Button>
        </Row>
      );
    }
    if (li.tipo !== "cambio" || li.moneda !== "USD") return null;
    return (
      <Row espacio={2}>
        <Button variante="secundario" tamanio="sm" cargando={t.cargandoEditar} aria-label={`Editar cambio: ${li.concepto}`} onClick={() => void t.abrirEditar(li)}>
          Editar
        </Button>
        <Button variante="fantasma" tamanio="sm" aria-label={`Borrar cambio: ${li.concepto}`} onClick={() => t.abrirBorrar(li)}>
          Borrar
        </Button>
      </Row>
    );
  };

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Caja</h1>
        {t.esAdmin && <Button onClick={t.abrirNuevo}>Registrar cambio</Button>}
      </Row>

      {t.cargandoSaldos && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}

      {s && (
        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard titulo="Saldo en pesos" valor={formatMoneda(s.saldo_ars, "ARS")} />
          <MetricCard titulo="Saldo en dólares" valor={formatMoneda(s.saldo_usd, "USD")} />
          <MetricCard titulo="Total en dólares" valor={s.total_usd === null ? "—" : formatMoneda(s.total_usd, "USD")} detalle={detalleTotalUsd(s)} />
        </div>
      )}

      <Alerta texto={s ? avisoNegativos(s.negativos) : null} tono="alerta" />

      {s && <PanelCajas panel={t.panel} saldos={s.por_caja} cajas={t.cajas} esAdmin={t.esAdmin} />}

      <Row envolver espacio={3} alinear="fin">
        <TextField etiqueta="Desde" type="date" value={t.filtros.desde} onChange={(e) => t.cambiarFiltro("desde", e.target.value)} />
        <TextField etiqueta="Hasta" type="date" value={t.filtros.hasta} onChange={(e) => t.cambiarFiltro("hasta", e.target.value)} />
        <Select
          etiqueta="Moneda"
          opciones={OPCIONES_MONEDA_CAJA}
          valor={t.filtros.moneda}
          onCambio={(v) => t.cambiarFiltro("moneda", v as Moneda | "")}
          placeholder="Todas las monedas"
          className="min-w-44"
        />
        <Select etiqueta="Tipo" opciones={OPCIONES_TIPO} valor={t.filtros.tipo} onCambio={(v) => t.cambiarFiltro("tipo", v as TipoLinea | "")} placeholder="Todos" className="min-w-40" />
        {t.mostrarCaja && (
          <Select etiqueta="Caja" opciones={t.opcionesFiltroCaja} valor={t.filtros.cajaId} onCambio={(v) => t.cambiarFiltro("cajaId", v)} placeholder="Todas las cajas" className="min-w-44" />
        )}
      </Row>

      {t.errorFiltros && <Alerta texto={t.errorFiltros} />}

      {!t.errorFiltros && t.cargandoLibro && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {!t.errorFiltros && t.errorCargaLibro && <Alerta texto={t.errorCargaLibro} />}
      <Alerta texto={t.errorEditar} />

      {!t.errorFiltros && l && (
        <Stack espacio={3}>
          <DataList
            etiqueta="Libro de caja"
            columnas={columnas}
            filas={l.items}
            claveFila={(li) => li.clave}
            acciones={t.esAdmin ? acciones : undefined}
            vacio="No hay movimientos en ese período."
          />
          <div className="grid gap-1 text-chico text-texto-suave">
            <p>
              Saldo inicial: {formatMoneda(l.saldo_inicial.ARS, "ARS")} · {formatMoneda(l.saldo_inicial.USD, "USD")}
            </p>
            <p>
              Ingresos del período: {formatMoneda(l.totales.ARS.ingresos, "ARS")} · {formatMoneda(l.totales.USD.ingresos, "USD")} · Total {formatMoneda(l.totales.ingresos_usd, "USD")}
            </p>
            <p>
              Egresos del período: {formatMoneda(l.totales.ARS.egresos, "ARS")} · {formatMoneda(l.totales.USD.egresos, "USD")} · Total {formatMoneda(l.totales.egresos_usd, "USD")}
            </p>
            <p>Los totales en USD no cuentan los cambios de moneda ni las transferencias entre cajas.</p>
            {t.acreditadoSinCaja.length > 0 && (
              <>
                {t.acreditadoSinCaja.map((linea) => (
                  <p key={linea}>{linea}</p>
                ))}
                <p>Eso se acreditó a fiduciantes sin que entrara plata: está en Aportes y Gastos, pero no en este libro ni en los saldos.</p>
              </>
            )}
          </div>
        </Stack>
      )}

      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "form" && d.cambio ? "Editar cambio" : "Registrar cambio"}>
        <FormularioCambio
          form={t.form}
          cambiar={t.cambiar}
          errores={t.errores}
          onSubmit={(e) => void t.guardar(e)}
          onCancelar={t.cerrar}
          guardando={t.guardando}
          error={t.errorGuardar}
          cambio={d?.tipo === "form" ? d.cambio : null}
          opcionesCaja={t.variasCajas ? t.opcionesCajaCambio : null}
          rutaDescarga={t.rutaDescarga}
          onSubirAdjunto={t.subirAdjunto}
          onBorrarAdjunto={t.borrarAdjunto}
          subiendoAdjunto={t.subiendoAdjunto}
          errorAdjuntos={t.errorAdjuntos}
        />
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar cambio">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>{`¿Borrar el cambio "${d.linea.concepto}" del ${formatFecha(d.linea.fecha)}? No se puede deshacer.`}</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
